import { API_URL } from "../../config";

/* ═══════════════════════════════════════════════════════════════════════════
   EMAIL HUB v2 — API SERVICE & TYPES
   ═══════════════════════════════════════════════════════════════════════════ */

const hdrs = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { ...opts, headers: { ...hdrs(), ...(opts.headers as Record<string, string> || {}) } });
  const json = await res.json();
  if (!res.ok && res.status !== 200) throw { status: res.status, ...json };
  return json.data;
}

/* ── Types ──────────────────────────────────────────────────────────────── */

export interface EmailAccount {
  id: number; email: string; display_name: string | null;
  is_active: boolean; last_synced_at: string | null;
  emails_count?: number; threads_count?: number;
}

export interface EmailReadReceipt {
  id: number;
  email_id: number;
  tracking_id: string;
  recipient_email: string | null;
  first_read_at: string | null;
  last_read_at: string | null;
  read_count: number;
  ip_address: string | null;
  user_agent: string | null;
}

export interface EmailMsg {
  id: number; account_id: number; message_id: string; thread_id: number;
  conversation_id: string | null; internet_message_id: string | null; in_reply_to: string | null;
  from_name: string | null; from_email: string;
  to_emails: { name: string; email: string }[]; cc_emails: { name: string; email: string }[] | null;
  subject: string; body_html: string | null; body_preview: string | null;
  direction: "incoming" | "outgoing"; folder: string | null;
  is_read: boolean; has_attachments: boolean; received_at: string;
  attachments?: EmailAttachment[];
  read_receipts?: EmailReadReceipt[];
}

export interface EmailAttachment {
  id: number; email_id: number; file_name: string; file_url: string | null;
  graph_attachment_id: string | null; is_downloaded: boolean;
  content_type: string | null; file_size: number;
}

export interface EmailThreadStatus {
  id: number; name: string; color: string; sort_order: number; is_default: boolean;
}

export interface EmailThreadNote {
  id: number; thread_id: number; user_id: number | null; note: string;
  created_at: string; user?: { id: number; name: string };
}

export interface EmailThread {
  id: number; thread_id: string; subject: string;
  customer_id: number | null; customer_email: string | null;
  account_id: number | null; message_count: number; unread_count: number;
  last_message_at: string | null; first_response_at: string | null;
  last_response_at: string | null; response_delay_minutes: number | null;
  is_overdue: boolean; sla_hours: number;
  tags: string[] | null; ai_intent: string | null; ai_summary: string | null;
  ai_extracted_data: AIExtractedData | null; status_id: number | null;
  is_archived: boolean;
  linked_thread_id?: number | null; is_private?: boolean;
  latest_email?: EmailMsg | null; account?: EmailAccount | null;
  status?: EmailThreadStatus | null; emails?: EmailMsg[];
  notes?: EmailThreadNote[]; customer?: any;
}

export interface AIExtractedData {
  event_type?: string | null; guest_count?: number | null;
  event_date?: string | null; urgency?: string | null;
}

export interface AIAnalysis {
  intent: string; event_type: string | null; guest_count: number | null;
  event_date: string | null; urgency: string; summary: string; suggested_reply: string;
}

export interface ThreadsResponse {
  data: EmailThread[]; current_page: number; last_page: number;
  per_page: number; total: number;
}

export interface EmailStats {
  total_unread: number; total_overdue: number;
  accounts: { id: number; email: string; unread: number; total: number }[];
}

export interface EmailTag { id: number; name: string; color: string; }

export interface EmailHubTemplate {
  id: number; name: string; subject: string; body: string;
  category: string | null; is_active: boolean;
}

export interface PipelineColumn {
  status: EmailThreadStatus; threads: EmailThread[]; count: number;
}

export interface ThreadFilters {
  account_id?: number; direction?: string; unread?: string; overdue?: string;
  tag?: string; intent?: string; status_id?: number; search?: string;
  customer_id?: number; page?: number; per_page?: number;
}

/* ── Helpers ────────────────────────────────────────────────────────────── */
const qs = (f: Record<string, any>): string => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
  const s = p.toString(); return s ? `?${s}` : "";
};

/* ── API Calls ──────────────────────────────────────────────────────────── */

// Stats
export const fetchStats = (): Promise<EmailStats> => apiFetch("/email-hub/stats");

// Threads
export const fetchThreads = (f: ThreadFilters = {}): Promise<ThreadsResponse> => apiFetch(`/email-hub/threads${qs(f)}`);
export const fetchThreadDetail = (id: number): Promise<EmailThread> => apiFetch(`/email-hub/threads/${id}`);
export const markThreadRead = (id: number): Promise<void> => apiFetch(`/email-hub/threads/${id}/read`, { method: "PATCH" });
export const updateThreadTags = (id: number, tags: string[]): Promise<EmailThread> => apiFetch(`/email-hub/threads/${id}/tags`, { method: "PATCH", body: JSON.stringify({ tags }) });
export const updateThreadStatus = (id: number, status_id: number): Promise<EmailThread> => apiFetch(`/email-hub/threads/${id}/status`, { method: "PATCH", body: JSON.stringify({ status_id }) });
export const archiveThread = (id: number): Promise<void> => apiFetch(`/email-hub/threads/${id}/archive`, { method: "PATCH" });

// Notes
export const addThreadNote = (threadId: number, note: string): Promise<EmailThreadNote> => apiFetch(`/email-hub/threads/${threadId}/notes`, { method: "POST", body: JSON.stringify({ note }) });
export const deleteThreadNote = (noteId: number): Promise<void> => apiFetch(`/email-hub/notes/${noteId}`, { method: "DELETE" });

// Send
export const sendEmail = (data: {
  account_id: number; to: string[]; cc?: string[]; subject: string; body: string;
  thread_id?: number; attachments?: File[];
  /** Template the rep applied — tracked on the email row + activity. */
  template_id?: number | null;
  /** True when the body came from the AI Draft assistant. */
  ai_assisted?: boolean;
  /** CRM linkage — forwarded by Client Profile / Lead View / Quick
   *  Actions surfaces so the persisted email row carries customer +
   *  lead foreign keys. Both nullable: Email Hub Compose without a
   *  linked thread sends them through as null and the audit row falls
   *  back to whatever can be inferred from thread.customer_id. */
  customer_id?: number | null;
  lead_id?: number | null;
}): Promise<EmailMsg> => {
  const { attachments, ...rest } = data;
  if (attachments && attachments.length > 0) {
    const fd = new FormData();
    fd.append("account_id", String(rest.account_id));
    rest.to.forEach(e => fd.append("to[]", e));
    if (rest.cc) rest.cc.forEach(e => fd.append("cc[]", e));
    fd.append("subject", rest.subject);
    fd.append("body", rest.body);
    if (rest.thread_id)       fd.append("thread_id",   String(rest.thread_id));
    if (rest.template_id)     fd.append("template_id", String(rest.template_id));
    if (rest.ai_assisted)     fd.append("ai_assisted", "1");
    if (rest.customer_id)     fd.append("customer_id", String(rest.customer_id));
    if (rest.lead_id)         fd.append("lead_id",     String(rest.lead_id));
    attachments.forEach(f => fd.append("attachments[]", f));
    return fetch(`${API_URL}/email-hub/send`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: fd,
    }).then(r => r.json()).then(j => { if (j.data) return j.data; throw j; });
  }
  return apiFetch("/email-hub/send", { method: "POST", body: JSON.stringify(rest) });
};

// Accounts
export const fetchAccounts = (): Promise<EmailAccount[]> => apiFetch("/email-hub/accounts");
export const createAccount = (data: { email: string; display_name?: string; tenant_id?: string; client_id?: string }): Promise<EmailAccount> => apiFetch("/email-hub/accounts", { method: "POST", body: JSON.stringify(data) });
export const deleteAccount = (id: number): Promise<void> => apiFetch(`/email-hub/accounts/${id}`, { method: "DELETE" });
export const getOAuthUrl = (id: number): Promise<{ url: string }> => apiFetch(`/email-hub/oauth/authorize/${id}`);

// Sync
export const triggerSync = (): Promise<any> => apiFetch("/email-hub/sync", { method: "POST" });

// Tags + Statuses
export const fetchTags = (): Promise<EmailTag[]> => apiFetch("/email-hub/tags");
export const createTag = (data: { name: string; color: string }): Promise<EmailTag> => apiFetch("/email-hub/tags", { method: "POST", body: JSON.stringify(data) });
export const fetchStatuses = (): Promise<EmailThreadStatus[]> => apiFetch("/email-hub/statuses");

// Pipeline
export const fetchPipeline = (accountId?: number): Promise<{ columns: PipelineColumn[]; no_status: EmailThread[] }> =>
  apiFetch(`/email-hub/pipeline${accountId ? `?account_id=${accountId}` : ""}`);

// Attachments (on-demand download)
export const downloadAttachment = (id: number): Promise<{ url: string; file_name: string }> =>
  apiFetch(`/email-hub/attachments/${id}/download`);

// Templates
export const fetchTemplates = (): Promise<EmailHubTemplate[]> => apiFetch("/email-hub/templates");
export const createTemplate = (data: { name: string; subject: string; body: string; category?: string }): Promise<EmailHubTemplate> =>
  apiFetch("/email-hub/templates", { method: "POST", body: JSON.stringify(data) });
export const updateTemplate = (id: number, data: Partial<EmailHubTemplate>): Promise<EmailHubTemplate> =>
  apiFetch(`/email-hub/templates/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteTemplate = (id: number): Promise<void> => apiFetch(`/email-hub/templates/${id}`, { method: "DELETE" });

// AI
export const aiAnalyze = (threadId: number): Promise<AIAnalysis> => apiFetch("/email-hub/ai/analyze", { method: "POST", body: JSON.stringify({ thread_id: threadId }) });
export const aiSuggestReply = (emailId: number): Promise<{ suggestion: string }> => apiFetch("/email-hub/ai/suggest-reply", { method: "POST", body: JSON.stringify({ email_id: emailId }) });
export const aiSummarize = (threadId: number): Promise<{ summary: string }> => apiFetch("/email-hub/ai/summarize", { method: "POST", body: JSON.stringify({ thread_id: threadId }) });
export const aiDetectIntent = (emailId: number): Promise<{ intent: string }> => apiFetch("/email-hub/ai/detect-intent", { method: "POST", body: JSON.stringify({ email_id: emailId }) });
export const aiCheckLead = (emailId: number): Promise<any> => apiFetch("/email-hub/ai/check-lead", { method: "POST", body: JSON.stringify({ email_id: emailId }) });
